import type { User } from '@/modules/Auth/userTypes';

import { decodeJwtPayload } from './jwtPayload';

function claim(payload: Record<string, unknown>, camel: string, snake: string): string {
  const a = payload[camel];
  const b = payload[snake];
  if (a !== undefined && a !== null && String(a) !== '') return String(a);
  if (b !== undefined && b !== null && String(b) !== '') return String(b);
  return '';
}

const emptyPool = {
  userPoolId: '',
  clientId: '',
  client: { endpoint: '', fetchOptions: {} },
  advancedSecurityDataCollectionFlag: false,
  storage: { domain: '', path: '/', expires: 365, secure: false, sameSite: 'lax' },
};

/** Build a Cognito-shaped User from a local dev JWT (HS256 from api/scripts/dev-token.py). */
export function buildUserFromDevJwt(token: string): User | null {
  const jwt = token.trim();
  if (!jwt) {
    return null;
  }

  const payload = decodeJwtPayload(jwt);
  if (!payload) {
    return null;
  }

  const sub = String(payload.sub || 'dev-user');
  const email = String(payload.email || payload['cognito:username'] || sub);
  const name = String(payload.name || 'Dev User');
  const groups = (payload['cognito:groups'] as string[]) || [];
  const tenantId = claim(payload, 'custom:tenantId', 'custom:tenant_id');
  const userId = claim(payload, 'custom:userId', 'custom:user_id') || sub;
  const userRole = claim(payload, 'custom:userRole', 'custom:user_role') || (groups[0] ?? 'admin');
  const now = Math.floor(Date.now() / 1000);

  const idPayload = {
    sub,
    'cognito:groups': groups,
    email_verified: true,
    iss: 'churnsignal-dev-local',
    'cognito:username': email,
    given_name: name.split(' ')[0] || 'Dev',
    'custom:userId': userId,
    origin_jti: '',
    'custom:tenantId': tenantId,
    aud: 'churnsignal-dev',
    event_id: '',
    'custom:userRole': userRole,
    token_use: 'id',
    auth_time: Number(payload.iat || now),
    exp: Number(payload.exp || now + 86400),
    iat: Number(payload.iat || now),
    family_name: name.split(' ').slice(1).join(' ') || 'User',
    jti: '',
    email,
  };

  const accessPayload = {
    sub,
    'cognito:groups': groups,
    iss: 'churnsignal-dev-local',
    client_id: 'churnsignal-dev',
    origin_jti: '',
    event_id: '',
    token_use: 'access',
    scope: '',
    auth_time: Number(payload.iat || now),
    exp: Number(payload.exp || now + 86400),
    iat: Number(payload.iat || now),
    jti: '',
    'custom:userRole': userRole,
    username: email,
  };

  return {
    username: email,
    pool: emptyPool,
    Session: null,
    client: { endpoint: '', fetchOptions: {} },
    signInUserSession: {
      idToken: { jwtToken: jwt, payload: idPayload },
      refreshToken: { token: '' },
      accessToken: { jwtToken: jwt, payload: accessPayload },
      clockDrift: 0,
    },
    attributes: {
      email,
      email_verified: true,
      family_name: idPayload.family_name,
      given_name: idPayload.given_name,
      sub,
      custom: {
        tenantId,
        userId,
        userRole,
      },
    },
  };
}
